import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { TableOrderService } from 'services';
import { FirebaseService } from 'services/FirebaseService';
import { ApiClient } from 'ApiClient';
import { UserTypes } from 'enums';

/**
 * Takeover for a dine-in guest order, deliberately the same experience as a
 * new website order: the screen is grabbed, the alarm loops, and it does not
 * let go until somebody accepts or declines (Lucy 2026-09-06).
 *
 * It shows on the restaurant's own login AND on every waiter login, and
 * whoever gets there first wins - the others' next refresh finds nothing
 * pending and closes itself.
 *
 * Kept separate from NewExternalOrderAlert on purpose: that component carries
 * the Bolt and Wolt accept deadlines, and this had no business adding risk to
 * it.
 */
// adminpeking@ removed 2026-09-21 (Lucy: "no popups on admin"): it is an
// office account, not a screen anyone watches during service.
const ALERT_EMAILS = [
  'pekingrestaurant@pekingltd.com',
  'pekingall@pekingltd.com',
];

const POLL_MS = 20000;

const useStyles = makeStyles((theme) => ({
  title: { background: '#7b1fa2', color: '#fff', padding: theme.spacing(2, 3) },
  table: { fontSize: '2rem', fontWeight: 800, lineHeight: 1.1 },
  sub: { opacity: 0.9, fontSize: '0.9rem' },
  line: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0),
  },
  qty: { fontWeight: 800, minWidth: 34 },
  note: { color: theme.palette.text.secondary, fontSize: '0.8rem' },
  actions: { padding: theme.spacing(2, 3) },
}));

interface PendingOrder {
  tableOrderId: number;
  tableLabel: string;
  submittedAt: string | null;
  total: number;
  items: Array<{
    title: string;
    quantity: number;
    notes: string | null;
    target: string;
    lineTotal: number;
  }>;
}

export const GuestOrderAlert: React.FC = () => {
  const classes = useStyles();
  const [allowed, setAllowed] = React.useState(false);
  const [pending, setPending] = React.useState<PendingOrder | null>(null);
  const [assist, setAssist] = React.useState<any | null>(null);
  // Snoozed requests, by id, until this timestamp. Kept in memory only: a
  // snooze is "not me, not now", not a decision worth persisting.
  const snoozedRef = React.useRef<{ [id: number]: number }>({});
  const [busy, setBusy] = React.useState(false);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  // Who hears it: the restaurant's own logins, and every waiter.
  React.useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const me: any = await ApiClient.get('users/with-token');
        if (dead) return;
        const email = (me?.emailAddress || '').toLowerCase();
        const isWaiter = me?.userType?.id === UserTypes.WAITER;
        setAllowed(isWaiter || ALERT_EMAILS.includes(email));
      } catch (e) {
        if (!dead) setAllowed(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, []);

  const refresh = React.useCallback(async () => {
    try {
      const rows: any = await TableOrderService.pendingGuestOrders();
      const first = Array.isArray(rows) && rows.length ? rows[0] : null;
      setPending(first);
    } catch (e) {
      // A failed poll must never clear a ringing alert.
    }
    try {
      const asks: any = await TableOrderService.assistanceRequests();
      const now = Date.now();
      const next = (Array.isArray(asks) ? asks : []).find(
        (a: any) => !snoozedRef.current[a.id] || snoozedRef.current[a.id] < now,
      );
      setAssist(next || null);
    } catch (e) {
      // Same: a failed poll leaves whatever is on screen alone.
    }
  }, []);

  // Firebase tells us the moment a guest sends; the poll is the safety net.
  React.useEffect(() => {
    if (!allowed) return undefined;
    refresh();
    const ref = FirebaseService.getDatabase().ref('external_orders/table/2');
    const handler = () => refresh();
    ref.on('value', handler);
    const timer = setInterval(refresh, POLL_MS);
    return () => {
      ref.off('value', handler);
      clearInterval(timer);
    };
  }, [allowed, refresh]);

  // Ring while something is waiting; stop the moment it is dealt with.
  React.useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (pending) {
      // Only an order waiting on approval rings. A request for napkins gets a
      // popup, not an alarm (Lucy 2026-09-07).
      el.play().catch(() => undefined);
    } else {
      el.pause();
      el.currentTime = 0;
    }
  }, [pending]);

  const accept = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await TableOrderService.approveGuest(pending.tableOrderId);
      await TableOrderService.fireCourse(pending.tableOrderId, 1);
      await TableOrderService.sendBar(pending.tableOrderId);
      setPending(null);
      refresh();
    } catch (e) {
      // Leave it up: an order that failed to send must stay in someone's face.
    } finally {
      setBusy(false);
    }
  };

  const onMyWay = async () => {
    if (!assist) return;
    setBusy(true);
    try {
      await TableOrderService.resolveAssistance(assist.id);
      setAssist(null);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  const snooze = () => {
    if (!assist) return;
    // Two minutes: long enough to finish what you are doing, short enough
    // that the table is not forgotten.
    snoozedRef.current[assist.id] = Date.now() + 2 * 60 * 1000;
    setAssist(null);
  };

  const decline = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await TableOrderService.rejectGuest(pending.tableOrderId);
      setPending(null);
      refresh();
    } finally {
      setBusy(false);
    }
  };

  if (!allowed) return null;

  return (
    <>
      <audio ref={audioRef} src="/sounds/order-alert.wav" loop />
      {/*
        Assistance: a popup, no alarm, and it can be snoozed. It waits behind
        an order approval so a waiter is never asked two things at once.
      */}
      <Dialog open={!!assist && !pending} maxWidth="xs" fullWidth>
        {assist && (
          <>
            <DialogTitle disableTypography className={classes.title} style={{ background: '#0277bd' }}>
              <div className={classes.sub}>
                Table asking &middot; {assist.askedAt}
                {assist.waitingMinutes > 0 ? ` · waiting ${assist.waitingMinutes} min` : ''}
              </div>
              <div className={classes.table}>Table {assist.tableLabel}</div>
            </DialogTitle>
            <DialogContent>
              <Typography variant="h6" style={{ padding: '18px 0', fontWeight: 700 }}>
                {assist.label}
              </Typography>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button onClick={snooze} disabled={busy}>
                Snooze 2 min
              </Button>
              <Button
                onClick={onMyWay}
                disabled={busy}
                variant="contained"
                style={{ background: '#0277bd', color: '#fff', minWidth: 180, minHeight: 46 }}
              >
                On my way
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={!!pending} maxWidth="sm" fullWidth disableEscapeKeyDown>
        {pending && (
          <>
            <DialogTitle disableTypography className={classes.title}>
              <div className={classes.sub}>Guest order from the table</div>
              <div className={classes.table}>Table {pending.tableLabel}</div>
            </DialogTitle>
            <DialogContent>
              <div style={{ padding: '12px 0' }}>
                {pending.items.map((i, idx) => (
                  <div key={idx}>
                    <div className={classes.line}>
                      <span>
                        <span className={classes.qty}>{i.quantity}×</span> {i.title}
                        {i.target === 'bar' ? (
                          <Chip size="small" label="BAR" style={{ marginLeft: 8 }} />
                        ) : null}
                      </span>
                      <span>€{i.lineTotal.toFixed(2)}</span>
                    </div>
                    {i.notes ? <div className={classes.note}>✎ {i.notes}</div> : null}
                  </div>
                ))}
              </div>
              <Divider />
              <div className={classes.line}>
                <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" style={{ fontWeight: 800 }}>
                  €{pending.total.toFixed(2)}
                </Typography>
              </div>
            </DialogContent>
            <DialogActions className={classes.actions}>
              <Button onClick={decline} disabled={busy} style={{ color: '#c62828' }}>
                Decline
              </Button>
              <Button
                onClick={accept}
                disabled={busy}
                variant="contained"
                style={{ background: '#1b5e20', color: '#fff', minWidth: 220, minHeight: 48 }}
              >
                {busy ? 'Sending…' : 'Accept & send to kitchen'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
